import { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, MousePointerClick, Eye, TrendingUp,
  MapPin, Copy, Check, ExternalLink, ChevronUp, ChevronDown, Minus,
} from 'lucide-react';
import { getBusinessDetails } from '@/utils/businessAppApi';
import { fetchPartnerAnalytics, fetchPartnerLeads, PartnerAnalytics, PartnerLead } from '@/api/partner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

type Days = 7 | 30 | 90;

const PRIMARY = '#2C5F2D';

// ─── helpers ─────────────────────────────────────────────────────────────────

function trend(current: number, previous: number): { pct: number; dir: 'up' | 'down' | 'flat' } {
  if (previous === 0 && current === 0) return { pct: 0, dir: 'flat' };
  if (previous === 0) return { pct: 100, dir: 'up' };
  const pct = Math.round(((current - previous) / previous) * 100);
  return { pct: Math.abs(pct), dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

function TrendBadge({ current, previous }: { current: number; previous: number }) {
  const t = trend(current, previous);
  if (t.dir === 'flat' && t.pct === 0) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-semibold',
      t.dir === 'up'   ? 'text-emerald-600' :
      t.dir === 'down' ? 'text-red-500' : 'text-slate-400')}>
      {t.dir === 'up'   ? <ChevronUp   className="w-3 h-3" /> :
       t.dir === 'down' ? <ChevronDown className="w-3 h-3" /> :
                          <Minus       className="w-3 h-3" />}
      {t.pct}%
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy}
      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border transition-all"
      style={copied
        ? { background: '#f0fdf4', borderColor: '#86efac', color: '#16a34a' }
        : { background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}>
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied!' : 'Copy link'}
    </button>
  );
}

// ─── stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, previous, icon, iconBg, loading, hint }: {
  label: string; value: number; previous: number;
  icon: React.ReactNode; iconBg: string;
  loading: boolean; hint?: string;
}) {
  return (
    <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 hover:shadow-lg transition-shadow">
      <CardContent className="pt-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
          <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center shadow-sm', iconBg)}>
            {icon}
          </div>
        </div>
        {loading ? (
          <div className="h-8 w-14 bg-slate-100 animate-pulse rounded" />
        ) : (
          <div className="flex items-end gap-2">
            <p className="text-3xl font-bold text-slate-900">{value.toLocaleString()}</p>
            <div className="mb-0.5">
              <TrendBadge current={value} previous={previous} />
            </div>
          </div>
        )}
        {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// ─── source breakdown bar ─────────────────────────────────────────────────────

const SOURCE_CONFIG: Record<string, { label: string; color: string }> = {
  map:    { label: 'Map',    color: '#2C5F2D' },
  search: { label: 'Search', color: '#3b82f6' },
  list:   { label: 'List',   color: '#8b5cf6' },
  direct: { label: 'Direct', color: '#f59e0b' },
};

function SourceBreakdown({ data, total }: { data: { source: string; count: number }[]; total: number }) {
  if (!total) return null;
  const sorted = [...data].sort((a, b) => b.count - a.count);
  return (
    <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-700">How visitors found you</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* stacked bar */}
        <div className="flex h-2.5 rounded-full overflow-hidden gap-0.5">
          {sorted.map(s => {
            const pct = (s.count / total) * 100;
            const cfg = SOURCE_CONFIG[s.source] ?? { label: s.source, color: '#94a3b8' };
            return (
              <div key={s.source} style={{ width: `${pct}%`, backgroundColor: cfg.color, minWidth: pct > 2 ? undefined : 0 }}
                className="rounded-sm transition-all" title={`${cfg.label}: ${s.count}`} />
            );
          })}
        </div>
        {/* legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {sorted.map(s => {
            const cfg = SOURCE_CONFIG[s.source] ?? { label: s.source, color: '#94a3b8' };
            const pct = Math.round((s.count / total) * 100);
            return (
              <div key={s.source} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: cfg.color }} />
                <span className="text-xs text-slate-600 font-medium">{cfg.label}</span>
                <span className="text-xs text-slate-400">{s.count} ({pct}%)</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── lead event row ───────────────────────────────────────────────────────────

function leadSentence(lead: PartnerLead): { icon: string; text: string } {
  const source = lead.source === 'map' ? 'the map' :
                 lead.source === 'search' ? 'a search' :
                 lead.source === 'list' ? 'the listing page' : 'a direct link';

  if (lead.eventType === 'booking_click') {
    return {
      icon: '🎯',
      text: `Clicked "Visit Website" from ${source} → landed on your website`,
    };
  }
  if (lead.eventType === 'map_click') {
    return { icon: '📍', text: `Clicked your pin on the map` };
  }
  return { icon: '👀', text: `Visited your Wildy profile from ${source}` };
}

// ─── empty state ──────────────────────────────────────────────────────────────

function EmptyState({ days, bookingUrl }: { days: Days; bookingUrl?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
      <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center text-3xl">🌿</div>
      <div>
        <p className="font-semibold text-slate-800">No visitors tracked in the last {days} days</p>
        <p className="text-sm text-slate-500 mt-1 max-w-sm">
          Leads appear here as soon as someone interacts with your listing on Wildy.
        </p>
      </div>
      {bookingUrl && (
        <div className="bg-slate-50 rounded-xl px-4 py-3 text-left max-w-sm w-full">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Your tracking link is active</p>
          <p className="text-xs text-slate-600 font-mono break-all">
            {(() => { try { const u = new URL(bookingUrl); u.searchParams.set('ref','wildy'); return u.toString(); } catch { return bookingUrl + '?ref=wildy'; } })()}
          </p>
          <p className="text-xs text-slate-400 mt-1.5">Share your Wildy profile to start generating leads.</p>
        </div>
      )}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export function PartnerAnalyticsDashboard() {
  const [isPartner,   setIsPartner]   = useState<boolean | null>(null);
  const [bookingUrl,  setBookingUrl]  = useState<string>('');
  const [days,        setDays]        = useState<Days>(30);
  const [analytics,   setAnalytics]   = useState<PartnerAnalytics | null>(null);
  const [leads,       setLeads]       = useState<PartnerLead[]>([]);
  const [leadsPage,   setLeadsPage]   = useState(1);
  const [leadsTotal,  setLeadsTotal]  = useState(0);
  const [loadingA,    setLoadingA]    = useState(false);
  const [loadingL,    setLoadingL]    = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  useEffect(() => {
    getBusinessDetails().then((res) => {
      const biz = res?.data;
      setIsPartner(biz?.isPartnerListing === true);
      setBookingUrl(biz?.contact?.bookingUrl ?? '');
    }).catch(() => setIsPartner(false));
  }, []);

  const loadAnalytics = useCallback(() => {
    if (!isPartner) return;
    setLoadingA(true); setError(null);
    fetchPartnerAnalytics(days)
      .then(setAnalytics)
      .catch(() => setError('Could not load analytics. Please try again.'))
      .finally(() => setLoadingA(false));
  }, [isPartner, days]);

  const loadLeads = useCallback(() => {
    if (!isPartner) return;
    setLoadingL(true);
    fetchPartnerLeads(leadsPage, 20, days)
      .then((res) => { setLeads(res.leads); setLeadsTotal(res.pagination.total); })
      .catch(() => {})
      .finally(() => setLoadingL(false));
  }, [isPartner, days, leadsPage]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { setLeadsPage(1); }, [days]);

  if (isPartner === null) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;
  }

  if (!isPartner) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center px-8">
        <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center">
          <TrendingUp className="h-8 w-8 text-emerald-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-800">Partner Analytics</h2>
        <p className="text-slate-500 max-w-md">
          This feature is available for partner listings. Contact us at{' '}
          <a href="mailto:hello@wildy.world" className="text-emerald-600 underline">hello@wildy.world</a>{' '}
          to become a partner and start tracking the leads we generate for you.
        </p>
      </div>
    );
  }

  const s     = analytics?.summary;
  const prev  = (analytics as any)?.previousSummary;
  const total = s?.totalLeads ?? 0;
  const totalLeadsPages = Math.ceil(leadsTotal / 20);

  const refUrl = (() => {
    if (!bookingUrl) return null;
    try { const u = new URL(bookingUrl); u.searchParams.set('ref', 'wildy'); return u.toString(); }
    catch { return bookingUrl + '?ref=wildy'; }
  })();

  return (
    <div className="space-y-5">

      {/* ── Hero banner ── */}
      <div className="rounded-2xl p-4 sm:p-6 text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a4d1c 100%)` }}>
        <div className="flex flex-col gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-white/20 text-white border-white/30 text-xs">Partner Listing</Badge>
              <div className="flex gap-1">
                {([7, 30, 90] as Days[]).map((d) => (
                  <button key={d} onClick={() => setDays(d)}
                    className={cn('text-xs px-2.5 py-1 rounded-lg transition-all font-medium',
                      days === d ? 'bg-white text-emerald-800' : 'text-emerald-200 hover:text-white hover:bg-white/15')}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            {loadingA ? (
              <div className="h-12 w-48 bg-white/10 animate-pulse rounded-lg" />
            ) : (
              <>
                <p className="text-4xl font-bold tracking-tight">
                  {total.toLocaleString()}
                  <span className="text-lg font-normal text-emerald-200 ml-2">
                    visitor{total !== 1 ? 's' : ''} from Wildy
                  </span>
                </p>
                <p className="text-emerald-300 text-sm mt-1">
                  in the last {days} days
                  {prev && total > 0 && (
                    <span className="ml-2">
                      <TrendBadge current={total} previous={prev.totalLeads} />
                      <span className="text-emerald-400 text-xs ml-1">vs prev. period</span>
                    </span>
                  )}
                </p>
                <p className="text-emerald-400/70 text-xs mt-2">
                  Each interaction type counted once per browser per 5 min
                </p>
              </>
            )}
          </div>

          {/* Tracking link */}
          {refUrl && (
            <div className="bg-white/10 rounded-xl px-4 py-3 w-full">
              <p className="text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1.5">Your tracking link</p>
              <p className="text-xs text-white/80 font-mono break-all leading-relaxed mb-2">{refUrl}</p>
              <div className="flex gap-2">
                <CopyButton text={refUrl} />
                <a href={refUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border border-white/20 text-white/80 hover:text-white transition-colors">
                  <ExternalLink className="w-3 h-3" /> Test
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Profile Views"  value={s?.profileViews ?? 0}  previous={prev?.profileViews ?? 0}
          icon={<Eye className="w-4 h-4 text-blue-600" />}    iconBg="bg-blue-100 border border-blue-200/50"
          loading={loadingA} hint="Your Wildy page was opened" />
        <StatCard label="Map Clicks"     value={s?.mapClicks ?? 0}     previous={prev?.mapClicks ?? 0}
          icon={<MapPin className="w-4 h-4 text-emerald-600" />} iconBg="bg-emerald-100 border border-emerald-200/50"
          loading={loadingA} hint="Pin clicked on the map" />
        <StatCard label="Website Clicks" value={s?.bookingClicks ?? 0} previous={prev?.bookingClicks ?? 0}
          icon={<MousePointerClick className="w-4 h-4 text-violet-600" />} iconBg="bg-violet-100 border border-violet-200/50"
          loading={loadingA} hint="Clicked through to your site" />
      </div>

      {/* ── Source breakdown ── */}
      {!loadingA && (analytics as any)?.sourceBreakdown?.length > 0 && (
        <SourceBreakdown data={(analytics as any).sourceBreakdown} total={total} />
      )}

      {/* ── Chart ── */}
      {total > 0 && (
        <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">
              Daily visitors — last {days} days
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingA ? (
              <div className="flex items-center justify-center h-48"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={analytics?.dailySeries ?? []} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v.slice(5)} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Line type="monotone" dataKey="leads" stroke={PRIMARY} strokeWidth={2.5} dot={false} name="Visitors" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Lead events list ── */}
      <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700">Recent activity</CardTitle>
            {leadsTotal > 0 && <span className="text-xs text-slate-400">{leadsTotal} events in {days}d</span>}
          </div>
        </CardHeader>
        <CardContent>
          {loadingL ? (
            <div className="flex items-center justify-center h-16"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
          ) : leads.length === 0 ? (
            <EmptyState days={days} bookingUrl={bookingUrl} />
          ) : (
            <>
              {/* Counting rules — shown once above the feed */}
              <div className="bg-slate-50 rounded-lg px-3 py-2.5 mb-3 text-xs text-slate-500 space-y-1 leading-relaxed">
                <p><span className="font-semibold text-slate-600">How leads are counted:</span> each visitor browser generates at most one event of each type per 5 minutes — so double-clicking "Visit Website" counts as one, but a profile view + website click in the same session count as two separate leads.</p>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 pt-0.5">
                  <span><span className="font-medium text-slate-600">👀 Profile view</span> — someone opened your Wildy page</span>
                  <span><span className="font-medium text-slate-600">📍 Map click</span> — someone tapped your pin on the map</span>
                  <span><span className="font-medium text-slate-600">🎯 Website click</span> — someone clicked "Visit Website"</span>
                </div>
              </div>
              <div className="space-y-1">
                {leads.map((lead) => {
                  const { icon, text } = leadSentence(lead);
                  return (
                    <div key={lead._id} className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50/60 transition-colors">
                      <span className="text-base mt-0.5 shrink-0">{icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700 leading-snug">{text}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{format(new Date(lead.timestamp), 'MMM d, HH:mm')}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalLeadsPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <span className="text-xs text-slate-400">{leadsTotal} total</span>
                  <div className="flex items-center gap-2">
                    <button disabled={leadsPage === 1} onClick={() => setLeadsPage(p => p - 1)}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors">
                      ← Prev
                    </button>
                    <span className="text-xs text-slate-400">{leadsPage} / {totalLeadsPages}</span>
                    <button disabled={leadsPage >= totalLeadsPages} onClick={() => setLeadsPage(p => p + 1)}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors">
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
